import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { useActualizarItem } from "@/hooks/useCatalogo";
import { getApiErrorMessage } from "@/lib/api";
import {
  errorDeNombreDeItem,
  type ItemDeCatalogo,
  type RecursoDeCatalogo,
} from "@/lib/catalogos";

const schema = z.object({
  nombre: z.string().trim().min(1, "El nombre es obligatorio."),
});

type Values = z.infer<typeof schema>;

interface EditarItemDialogProps {
  recurso: RecursoDeCatalogo;
  /** Ítem a renombrar; `null` cierra el diálogo. */
  item: ItemDeCatalogo | null;
  /** Rótulo en singular del catálogo ("contexto", "ítem de revisión"). */
  singular: string;
  onCerrar: () => void;
}

/**
 * Renombrar un ítem de un catálogo ordenado.
 *
 * La posición no se toca — `orden` no viaja en la edición, ni acá ni en el
 * backend, así que renombrar nunca reordena el catálogo.
 */
export function EditarItemDialog({
  recurso,
  item,
  singular,
  onCerrar,
}: EditarItemDialogProps) {
  const actualizar = useActualizarItem(recurso);
  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { nombre: item?.nombre ?? "" },
  });

  if (!item) return null;

  function handleSubmit(valores: Values) {
    if (!item) return;

    actualizar.mutate(
      { id: item.id, nombre: valores.nombre },
      {
        onSuccess: (actualizado) => {
          toast.success(`«${actualizado.nombre}» actualizado`);
          onCerrar();
        },
        onError: (error) => {
          const mensaje = errorDeNombreDeItem(error);
          if (mensaje) {
            form.setError("nombre", { type: "server", message: mensaje });
            return;
          }
          toast.error(
            getApiErrorMessage(error, `No pudimos guardar el ${singular}. Inténtalo de nuevo.`),
          );
        },
      },
    );
  }

  return (
    <Dialog open onOpenChange={(abierto) => !abierto && !actualizar.isPending && onCerrar()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Renombrar {singular}</DialogTitle>
          <DialogDescription>
            Conserva su posición en el catálogo y su identidad: todo lo que ya lo apunte
            lo sigue apuntando.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-5">
            <FormField
              control={form.control}
              name="nombre"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nombre del {singular}</FormLabel>
                  <FormControl>
                    <Input autoComplete="off" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                disabled={actualizar.isPending}
                onClick={onCerrar}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={actualizar.isPending} className="gap-2">
                {actualizar.isPending && <Spinner className="text-primary-foreground" />}
                Guardar cambios
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
